import { ContentDocument, ContentDocumentMediaType } from '../content-document.model';
import { ContentDocumentBlogAdapter } from './adapters/content-document-blog.adapter';
import { ContentDocumentCommentAdapter } from './adapters/content-document-comment.adapter';
import { ContentDocumentEditorialBlogAdapter } from './adapters/content-document-editorial-blog.adapter';
import { ContentDocumentFacebookAdapter } from './adapters/content-document-facebook.adapter';
import { ContentDocumentForumAdapter } from './adapters/content-document-forum.adapter';
import { ContentDocumentInstagramAdapter } from './adapters/content-document-instagram.adapter';
import { ContentDocumentNewsAdapter } from './adapters/content-document-news.adapter';
import { ContentDocumentProductReviewAdapter } from './adapters/content-document-product-review.adapter';
import { ContentDocumentTveyesAdapter } from './adapters/content-document-tveyes.adapter';
import { ContentDocumentTwitterAdapter } from './adapters/content-document-twitter.adapter';
import { ContentDocumentYoutubeAdapter } from './adapters/content-document-youtube.adapter';
import { ContentDocumentTruncationTransformer } from './transformers/content-document-truncation.transformer';
import { ContentDocumentDefaultAdapter } from './adapters/content-document-default.adapter';

/**
 * @ngdoc service
 * @name mwContentDocumentAdapterService
 * @module mw.components.contentDocuments
 *
 * @description
 * A service used for mapping different media type document models to a standard content document model.
 */
/**
 * @ngdoc method
 * @name mwContentDocumentAdapterService#toContentDocument
 * @param {Object} document - The media type document object that should be adapted to a ContentDocument model.
 *
 * @returns {ContentDocument} A content document or null if unable to adapt.
 */

export class ContentDocumentAdapterService {
    private transformers = [];
    private adapterMapping = {};

    constructor() {
        this.registerTransformer(ContentDocumentTruncationTransformer);

        this.registerMediaTypeAdapter(ContentDocumentMediaType.Blog, ContentDocumentBlogAdapter);
        this.registerMediaTypeAdapter(ContentDocumentMediaType.Comments, ContentDocumentCommentAdapter);
        this.registerMediaTypeAdapter(ContentDocumentMediaType.Facebook, ContentDocumentFacebookAdapter);
        this.registerMediaTypeAdapter(ContentDocumentMediaType.Forums, ContentDocumentForumAdapter);
        this.registerMediaTypeAdapter(ContentDocumentMediaType.Instagram, ContentDocumentInstagramAdapter);
        this.registerMediaTypeAdapter(ContentDocumentMediaType.News, ContentDocumentNewsAdapter);
        this.registerMediaTypeAdapter(ContentDocumentMediaType.ProductReview, ContentDocumentProductReviewAdapter);
        this.registerMediaTypeAdapter(ContentDocumentMediaType.Radio, ContentDocumentTveyesAdapter);
        this.registerMediaTypeAdapter(ContentDocumentMediaType.Television, ContentDocumentTveyesAdapter);
        this.registerMediaTypeAdapter(ContentDocumentMediaType.Twitter, ContentDocumentTwitterAdapter);
        this.registerMediaTypeAdapter(ContentDocumentMediaType.Youtube, ContentDocumentYoutubeAdapter);
        this.registerMediaTypeAdapter(ContentDocumentMediaType.EditorialBlog, ContentDocumentEditorialBlogAdapter);
    }

    public toContentDocument(document: any): ContentDocument {
        let adapter;
        if (!(document && document.mediaType)) {
            console.warn('document or document mediaType is unknown');
            return document;
        }

        if (!this.adapterMapping[document.mediaType]) {
            adapter = ContentDocumentDefaultAdapter;
        } else {
            adapter = this.adapterMapping[document.mediaType];
        }

        // Transformers
        const transformedDocument = this.transformers.reduce((document, transformer) => {
            return transformer.transform(document);
        }, document);

        // Adapters
        const contentDocument = adapter.toContentDocument(transformedDocument);

        if (contentDocument && document.dupDocs && document.dupDocs.length) {
            contentDocument.dupDocs = document.dupDocs.map((dupDoc) => {
                let dupContentDocument = this.toContentDocument(dupDoc);
                dupContentDocument.isDuplicate = true;
                return dupContentDocument;
            });
        }

        return contentDocument;
    }

    public registerMediaTypeAdapter(mediaType: ContentDocumentMediaType | string, adapter: any) {
        if (!mediaType) {
            console.warn('mediaType is required');
            return;
        }
        if (!adapter || typeof adapter.toContentDocument !== 'function' ) {
            console.warn('adapter is required');
            return;
        }
        this.adapterMapping[mediaType] = adapter;
    }

    public registerTransformer(transformer: any) {
        if (!transformer || typeof transformer.transform !== 'function' ) {
            console.warn('transformer is required');
            return;
        }
        this.transformers.push(transformer);
    }

}



// WEBPACK FOOTER //
// ./src/lib/components/content-documents/media-document/adapter/content-document-adapter.service.ts